import { Popover, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { RefCallback } from 'preact'
import { useState } from 'preact/hooks'
import { usePopper } from 'react-popper'

import { InfoIcon } from './icons/Info'

interface TooltipProps {
  className?: string
  id?: string
  children: preact.ComponentChildren
  button?: preact.ComponentChildren
  title?: preact.ComponentChildren
}

export function Tooltip({ className, children, button, title, id }: TooltipProps) {
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement>()
  const [popperElement, setPopperElement] = useState<HTMLDivElement>()
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    strategy: 'absolute',
  })

  return (
    <Popover className="flex">
      <Popover.Button ref={setReferenceElement as unknown as RefCallback<HTMLButtonElement>}>
        {({ open }: { open: boolean }) => button || <Tooltip.Button open={open} className={className} />}
      </Popover.Button>
      <Transition
        enter="transition duration-200 ease-out"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-75 ease-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0">
        <Popover.Panel
          ref={setPopperElement as RefCallback<HTMLDivElement>}
          style={styles.popper}
          {...attributes.popper}>
          <div className="relative z-50 px-1 sm:px-0 w-80">
            <div
              id={id}
              role="tooltip"
              className="px-4 py-3 my-2 space-y-1 text-xs font-normal leading-normal divide-y rounded-md shadow-xl shadow-navy/20 sm:shadow-2xl sm:shadow-navy/60 sm:px-6 sm:py-4 text-black-80 sm:text-sm sm:leading-normal backdrop-blur bg-white/80 sm:bg-white/90 ring-1 ring-yellow divide-black-40 sm:divide-black-20">
              {!!title && <strong className="block">{title}</strong>}
              <div className="pt-2 space-y-1">{children}</div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}

interface TooltipButtonProps {
  className?: string
  open: boolean
}

Tooltip.Button = function TooltipButton({ className, open }: TooltipButtonProps) {
  return (
    <span
      className={clsx(
        'flex items-center justify-center transition-colors bg-transparent border rounded-full shrink-0 focus:outline-none hover:text-white',
        open ? 'border-yellow' : 'border-current',
      )}>
      <InfoIcon
        className={clsx(
          'transition-colors rounded-full',
          className || 'w-3 h-3 p-0.5',
          open ? 'bg-yellow-light text-navy' : 'bg-transparent',
        )}
      />
    </span>
  )
}
